<template>
  <div class="grid">
    <div class="col-12 mb-3">
      <span class="p-float-label">
        <InputText id="name" v-model="commodity.name" class="w-full" />
        <label for="name">Name*</label>
      </span>
      <small v-if="submitted && !commodity.name" class="p-error">Name is required.</small>
    </div>

    <div class="col-12 mb-3">
      <span class="p-float-label">
        <Select
          id="category"
          v-model="commodity.category"
          :options="categories"
          class="w-full"
          placeholder="Select a category"
          :loading="settingsStore.isLoading"
        />
        <label for="category">Category*</label>
      </span>
      <small v-if="submitted && !commodity.category" class="p-error">Category is required.</small>
    </div>

    <div class="col-12 mb-3">
      <span class="p-float-label">
        <InputNumber
          id="price"
          v-model="commodity.current_price_per_lb"
          mode="currency"
          currency="USD"
          locale="en-US"
          :minFractionDigits="2"
          class="w-full"
        />
        <label for="price">Price per lb ($)*</label>
      </span>
    </div>

    <div class="col-12 mb-3">
      <span class="p-float-label">
        <Textarea id="description" v-model="commodity.description" rows="3" class="w-full" />
        <label for="description">Description</label>
      </span>
    </div>

    <div class="col-12 mb-3">
      <div class="flex align-items-center">
        <ToggleSwitch v-model="commodity.is_active" />
        <label for="is_active" class="ml-2">Active</label>
      </div>
    </div>

    <div class="col-12 mb-3">
      <div class="flex align-items-center">
        <ToggleSwitch v-model="commodity.is_favorite" />
        <label for="is_favorite" class="ml-2">Favorite</label>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, watch, onMounted, computed } from 'vue'
import type { CommodityType } from '@/types/CommodityType'
import { useSettingsStore } from '@/stores/settings'
import InputText from 'primevue/inputtext'
import InputNumber from 'primevue/inputnumber'
import Select from 'primevue/select'
import Textarea from 'primevue/textarea'
import ToggleSwitch from 'primevue/toggleswitch'

const props = defineProps({
  initialCommodity: {
    type: Object as () => Partial<CommodityType>,
    default: () => ({
      name: '',
      description: '',
      category: '',
      current_price_per_lb: 0,
      is_active: true,
      is_favorite: false,
    }),
  },
  submitted: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['update:commodity'])

// Use settings store to get categories
const settingsStore = useSettingsStore()

// Computed property to get categories from settings
const categories = computed(() => settingsStore.commoditySettings.categories)

// Create a reactive copy of the commodity
const commodity = reactive<Partial<CommodityType>>({
  name: props.initialCommodity.name || '',
  description: props.initialCommodity.description || '',
  category: props.initialCommodity.category || '',
  current_price_per_lb: props.initialCommodity.current_price_per_lb || 0,
  is_active:
    props.initialCommodity.is_active !== undefined ? props.initialCommodity.is_active : true,
  is_favorite:
    props.initialCommodity.is_favorite !== undefined ? props.initialCommodity.is_favorite : false,
})

// Load settings on component mount
onMounted(async () => {
  await settingsStore.loadSettings()
})

// Watch for changes and emit updates
watch(
  commodity,
  (newValue) => {
    emit('update:commodity', { ...newValue })
  },
  { deep: true },
)

// Watch for changes in initialCommodity prop
watch(
  () => props.initialCommodity,
  (newValue) => {
    Object.assign(commodity, {
      name: newValue.name || '',
      description: newValue.description || '',
      category: newValue.category || '',
      current_price_per_lb: newValue.current_price_per_lb || 0,
      is_active: newValue.is_active !== undefined ? newValue.is_active : true,
      is_favorite: newValue.is_favorite !== undefined ? newValue.is_favorite : false,
    })
  },
  { deep: true },
)
</script>
